import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Banknote,
  BarChart3,
  Bell,
  Building2,
  ClipboardList,
  Command,
  HardHat,
  Inbox,
  LayoutDashboard,
  LogOut,
  Megaphone,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Radio,
  Receipt,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  Truck,
  UserPlus,
  Warehouse,
  X,
} from 'lucide-react';
import { api } from '../lib/api.js';
import { relative } from '../lib/format.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useDebounced, useHotkey, useLocalStorage, usePoll, useToggle } from '../lib/hooks.js';
import { Avatar, Badge, Button, Input, Modal, cx, useToast } from './ui.jsx';
import BrandMark from './BrandMark.jsx';

const NAV = [
  {
    group: 'Every day',
    items: [
      { to: '/app', label: 'Today', icon: LayoutDashboard, end: true },
      { to: '/app/dispatch', label: 'Jobs needing someone', icon: Radio, badge: 'atRisk', badgeTone: 'red' },
      { to: '/app/jobs', label: 'All jobs', icon: ClipboardList, badge: 'unfilled' },
      { to: '/app/live', label: "Where's everyone", icon: Truck },
    ],
  },
  {
    group: 'People',
    items: [
      { to: '/app/lumpers', label: 'Our lumpers', icon: HardHat },
      { to: '/app/onboarding', label: 'New people', icon: UserPlus, badge: 'applicants', badgeTone: 'amber' },
      { to: '/app/payroll', label: 'Paying lumpers', icon: Banknote },
    ],
  },
  {
    group: 'Customers',
    items: [
      { to: '/app/customers', label: 'Customers', icon: Building2 },
      { to: '/app/sites', label: 'Warehouses', icon: Warehouse },
      { to: '/app/invoices', label: 'Invoices & money owed', icon: Receipt, badge: 'overdueInvoices', badgeTone: 'red' },
    ],
  },
  {
    group: 'Keep in touch',
    items: [
      { to: '/app/notifications', label: 'Send a message', icon: Megaphone },
      { to: '/app/leads', label: 'People asking for work', icon: Inbox, badge: 'newLeads' },
    ],
  },
  {
    group: 'Owner',
    items: [
      { to: '/app/reports', label: 'How business is going', icon: BarChart3 },
      { to: '/app/settings', label: 'Settings', icon: SettingsIcon },
    ],
  },
];

const ALL_NAV = NAV.flatMap((section) => section.items);

export default function Layout() {
  const { actor, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [collapsed, setCollapsed] = useLocalStorage('3lumpers.rail', false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const { data: dash } = usePoll('/reports/dashboard', { ms: 60_000 });
  const { data: notifData } = usePoll('/notifications', { params: { status: 'sent', limit: 8 }, ms: 90_000 });

  const badges = useMemo(() => {
    const kpis = dash?.kpis || {};
    const alerts = dash?.alerts || {};
    const unread = notifData?.summary?.sent ?? 0;
    return {
      atRisk: alerts.atRisk?.length || 0,
      unfilled: kpis.unfilled || 0,
      applicants: kpis.applicants || 0,
      overdueInvoices: alerts.overdueInvoices?.length || 0,
      newLeads: 0,
      broadcasts: unread,
    };
  }, [dash, notifData]);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenu(false);
  }, [location.pathname]);

  useHotkey('k', { meta: true }, () => setPaletteOpen(true));
  useHotkey('b', { meta: true }, () => navigate('/app/jobs?new=1'));

  const onLogout = async () => {
    await logout();
    toast.info('Signed out');
    navigate('/login');
  };

  return (
    <div className="shell" data-collapsed={collapsed} data-mobile={mobileOpen ? 'open' : 'closed'}>
      <aside className="rail">
        <div className="rail-brand">
          <BrandMark size={40} />
          <span className="brand-text">
            <strong>3Lumpers</strong>
            <span>Control panel</span>
          </span>
        </div>

        <nav className="nav">
          {NAV.map((section) => (
            <div className="nav-group" key={section.group}>
              <span className="label">{section.group}</span>
              {section.items.map((item) => {
                const count = item.badge ? badges[item.badge] : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => cx('nav-item', isActive && 'active')}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon size={17} strokeWidth={2} />
                    <span>{item.label}</span>
                    {count ? (
                      <span
                        className="nav-count"
                        style={item.badgeTone === 'red' ? { background: 'rgba(251,95,95,.2)', color: '#ffb3b3' } : undefined}
                      >
                        {count}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="rail-foot">
          <button
            type="button"
            className="nav-item"
            onClick={() => setCollapsed(!collapsed)}
            style={{ background: 'none', border: '1px solid transparent', cursor: 'pointer', width: '100%' }}
          >
            {collapsed ? <PanelLeftOpen size={17} strokeWidth={2} /> : <PanelLeftClose size={17} strokeWidth={2} />}
            <span>Collapse</span>
          </button>
          <div className="user-card">
            <Avatar name={actor?.name} tone={actor?.accent || 'amber'} size="sm" />
            <div className="user-meta grow truncate">
              <div className="truncate" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{actor?.name}</div>
              <div className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{actor?.title || actor?.role}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <Button
            className="rail-toggle"
            variant="ghost"
            size="sm"
            icon={mobileOpen ? X : Command}
            onClick={() => setMobileOpen((v) => !v)}
          >
            Menu
          </Button>

          <button
            type="button"
            className="search grow"
            style={{ maxWidth: 420, background: 'none', border: 0, padding: 0, cursor: 'text' }}
            onClick={() => setPaletteOpen(true)}
          >
            <Search size={14} strokeWidth={2.2} />
            <input className="input" placeholder="Search jobs, lumpers or customers…" readOnly />
            <span className="kbd" style={{ position: 'absolute', right: 9 }}>⌘K</span>
          </button>

          <div className="grow" />

          <Badge tone="green" dot live className="nowrap hide-sm">
            <span className="hide-sm">We are open</span>
          </Badge>

          <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate('/app/jobs?new=1')}>
            <span className="hide-sm">Add a job</span>
          </Button>

          <div style={{ position: 'relative' }}>
            <Button variant="ghost" size="sm" icon={Bell} onClick={() => setNotifOpen((v) => !v)} aria-label="Notifications">
              {badges.broadcasts ? (
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    minWidth: 15,
                    height: 15,
                    borderRadius: 99,
                    background: 'var(--red)',
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 700,
                    display: 'grid',
                    placeItems: 'center',
                    padding: '0 3px',
                  }}
                >
                  {badges.broadcasts}
                </span>
              ) : null}
            </Button>
            {notifOpen ? (
              <NotifPopover
                notifications={notifData?.data || []}
                onClose={() => setNotifOpen(false)}
                onOpenAll={() => {
                  setNotifOpen(false);
                  navigate('/app/notifications');
                }}
              />
            ) : null}
          </div>

          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setUserMenu((v) => !v)}
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
              aria-label="Account menu"
            >
              <Avatar name={actor?.name} tone={actor?.accent || 'amber'} />
            </button>
            {userMenu ? (
              <div className="card flat anim-pop" style={{ position: 'absolute', right: 0, top: 44, width: 246, zIndex: 60 }}>
                <div className="card-body">
                  <div className="row gap-12">
                    <Avatar name={actor?.name} tone={actor?.accent || 'amber'} />
                    <div className="grow truncate">
                      <div className="truncate" style={{ fontWeight: 620 }}>{actor?.name}</div>
                      <div className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{actor?.email}</div>
                    </div>
                  </div>
                  <div className="divider" />
                  <Badge tone="blue">{actor?.role}</Badge>
                  <div className="col gap-6 mt-12">
                    <Button variant="ghost" size="sm" icon={SettingsIcon} onClick={() => navigate('/app/settings')}>
                      Workspace settings
                    </Button>
                    <Button variant="ghost" size="sm" icon={LogOut} onClick={onLogout}>
                      Sign out
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

function NotifPopover({ notifications, onClose, onOpenAll }) {
  return (
    <div className="card flat anim-pop" style={{ position: 'absolute', right: 0, top: 42, width: 348, zIndex: 60 }}>
      <div className="card-head">
        <h4 className="grow" style={{ fontSize: 'var(--fs-md)' }}>Recent broadcasts</h4>
        <Button variant="ghost" size="sm" onClick={onOpenAll}>View all</Button>
      </div>
      <div className="card-body col gap-12" style={{ maxHeight: 320, overflow: 'auto' }}>
        {notifications.length ? (
          notifications.map((n) => (
            <div key={n.id} className="row-top gap-8">
              <Megaphone size={13} style={{ color: 'var(--amber)', marginTop: 3 }} />
              <div className="grow">
                <div className="truncate" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{n.title}</div>
                <div className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{n.body}</div>
                <div className="dimmer" style={{ fontSize: 10 }}>
                  {relative(n.sentAt || n.createdAt)} · {n.recipients} recipients · {n.opened} opened
                </div>
              </div>
            </div>
          ))
        ) : (
          <span className="dim">Nothing sent yet.</span>
        )}
      </div>
      <div className="card-foot">
        <Button variant="outline" size="sm" className="btn-block" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- command palette */

function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ jobs: [], lumpers: [], customers: [] });
  const [index, setIndex] = useState(0);
  const debounced = useDebounced(query, 220);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || debounced.trim().length < 2) {
      setResults({ jobs: [], lumpers: [], customers: [] });
      return undefined;
    }
    let alive = true;
    Promise.all([
      api.get('/jobs', { q: debounced, limit: 5 }),
      api.get('/lumpers', { q: debounced, limit: 4 }),
      api.get('/customers', { q: debounced, limit: 4 }),
    ])
      .then(([jobs, lumpers, customers]) => {
        if (!alive) return;
        setResults({
          jobs: jobs.data || [],
          lumpers: lumpers.data || [],
          customers: customers.data || [],
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [debounced, open]);

  const items = useMemo(() => {
    const list = [];
    LIST_STATIC.forEach((item) => {
      if (!query || item.label.toLowerCase().includes(query.toLowerCase())) list.push(item);
    });
    results.jobs.forEach((job) =>
      list.push({
        kind: 'Job',
        label: `${job.code} · ${job.customerName || 'Unassigned'}`,
        hint: `${job.siteCity || ''} ${job.siteState || ''}`.trim() || job.status,
        to: `/app/jobs/${job.id}`,
        icon: ClipboardList,
      }),
    );
    results.lumpers.forEach((lumper) =>
      list.push({
        kind: 'Lumper',
        label: `${lumper.firstName} ${lumper.lastName}`,
        hint: `${lumper.city || ''} ${lumper.state || ''}`.trim() || lumper.status,
        to: `/app/lumpers/${lumper.id}`,
        icon: HardHat,
      }),
    );
    results.customers.forEach((customer) =>
      list.push({
        kind: 'Customer',
        label: customer.name,
        hint: `${customer.city || ''} ${customer.state || ''}`.trim(),
        to: `/app/customers/${customer.id}`,
        icon: Building2,
      }),
    );
    return list.slice(0, 22);
  }, [query, results]);

  const go = useCallback(
    (item) => {
      if (!item) return;
      onClose();
      navigate(item.to);
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setIndex((i) => Math.min(items.length - 1, i + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        go(items[index]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, items, index, go]);

  return (
    <Modal open={open} onClose={onClose} title="Jump to anything" subtitle="Search jobs, lumpers, customers, or a page">
      <Input
        autoFocus
        className="input"
        placeholder="Type a job number, lumper name, customer…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIndex(0);
        }}
      />
      <div className="col gap-2 mt-16" style={{ maxHeight: 420, overflow: 'auto' }}>
        {items.length ? (
          items.map((item, i) => (
            <button
              key={`${item.kind}-${item.to}-${i}`}
              type="button"
              onMouseEnter={() => setIndex(i)}
              onClick={() => go(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid transparent',
                background: i === index ? 'rgba(255,138,31,.12)' : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <item.icon size={15} strokeWidth={2} style={{ color: i === index ? 'var(--amber)' : 'var(--fg-3)' }} />
              <span className="grow truncate">{item.label}</span>
              <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{item.hint}</span>
              <span className="label">{item.kind}</span>
            </button>
          ))
        ) : (
          <div className="dim center" style={{ padding: 24 }}>No matches. Try a job number like JOB-10042.</div>
        )}
      </div>
    </Modal>
  );
}

const LIST_STATIC = ALL_NAV.map((item) => ({
  kind: 'Screen',
  label: item.label,
  hint: 'Go to',
  to: item.to,
  icon: item.icon,
}));
